import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

const PRESET_COLORS = ['#f97316', '#0ea5e9', '#10b981', '#8b5cf6', '#ef4444', '#eab308', '#14b8a6', '#3b82f6'];

export default function Categories({ user }) {
  const canManage = user?.role === 'ADMIN';
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: '', code: '', color: '#f97316' });
  const [editId, setEditId] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  async function load() {
    try {
      setItems(await api.listCategories());
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startEdit(item) {
    setEditId(item.id);
    setForm({ name: item.name, code: item.code, color: item.color });
  }

  async function save(e) {
    e.preventDefault();
    setError('');
    setStatus('');
    const payload = { name: form.name.trim(), color: form.color };
    if (form.code.trim()) payload.code = form.code.trim();
    try {
      if (editId) {
        await api.updateCategory(editId, payload);
        setStatus('Categoria atualizada.');
      } else {
        await api.createCategory(payload);
        setStatus('Categoria criada.');
      }
      setEditId('');
      setForm({ name: '', code: '', color: '#f97316' });
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function toggleActive(item) {
    setError('');
    setStatus('');
    try {
      await api.updateCategory(item.id, { active: !item.active });
      setStatus(item.active ? 'Categoria desativada.' : 'Categoria ativada.');
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function remove(item) {
    if (!confirm(`Excluir categoria "${item.name}"?`)) return;
    setError('');
    setStatus('');
    try {
      await api.deleteCategory(item.id);
      setStatus('Categoria excluída.');
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-sky-700">EtiquetandoApp Studio</div>
          <h1 className="studio-uniform-title mt-2 font-h2 text-h2 tracking-[-0.04em] text-on-surface">Categorias de etiqueta</h1>
          <p className="mt-2 text-[11px] leading-6 text-on-surface-variant">
            Categorias sío por tenant e ficam disponíveis ao criar e filtrar etiquetas no estúdio.
          </p>
        </div>
        <span className="rounded-full bg-primary-container px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-on-primary-container">
          {items.length} categoria{items.length === 1 ? '' : 's'}
        </span>
      </div>

      {error && <p className="rounded-2xl border border-error/20 bg-error-container/40 px-4 py-3 text-[11px] text-error">{error}</p>}
      {status && <p className="rounded-2xl border border-primary/10 bg-primary-container/40 px-4 py-3 text-[11px] text-on-primary-container">{status}</p>}

      {canManage && (
        <form onSubmit={save} className="overflow-hidden rounded-[30px] border border-surface-container-highest bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(246,242,237,0.96)_100%)] px-5 py-5 shadow-[0_22px_60px_rgba(15,23,42,0.10)]">
          <h2 className="studio-uniform-title text-[13px] font-semibold text-on-surface">{editId ? 'Editar categoria' : 'Nova categoria'}</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_auto_auto]">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nome (ex.: Logística reversa)"
              className="w-full rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
            />
            <input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="Código (opcional, ex.: LOGISTICA_REVERSA)"
              className="w-full rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
            />
            <div className="flex flex-wrap items-center gap-1.5">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  title={color}
                  onClick={() => setForm({ ...form, color })}
                  className={`h-6 w-6 rounded-full border transition ${form.color === color ? 'scale-110 ring-2 ring-primary ring-offset-1' : 'border-black/10'}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <button
              type="submit"
              className="rounded-2xl bg-primary px-5 py-3 text-[11px] font-semibold text-on-primary shadow-[0_12px_24px_rgba(249,115,22,0.2)]"
            >
              {editId ? 'Salvar alterações' : 'Criar categoria'}
            </button>
          </div>
          {editId && (
            <button type="button" onClick={() => setEditId('')} className="mt-3 text-[10px] font-semibold text-on-surface-variant hover:text-on-surface">
              Cancelar edição
            </button>
          )}
        </form>
      )}

      <div className="overflow-hidden rounded-[30px] border border-surface-container-highest bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(246,242,237,0.96)_100%)] shadow-[0_22px_60px_rgba(15,23,42,0.10)]">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-surface-container-high">
                <th className="px-5 py-3 text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Categoria</th>
                <th className="px-5 py-3 text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Código</th>
                <th className="px-5 py-3 text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Cor</th>
                <th className="px-5 py-3 text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Status</th>
                <th className="px-5 py-3 text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Etiquetas</th>
                <th className="px-5 py-3 text-right text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-[10px] text-on-surface-variant">Nenhuma categoria cadastrada.</td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="border-b border-surface-container-low transition hover:bg-surface-container-low">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: item.color }} />
                        <span className="text-[11px] font-semibold text-on-surface">{item.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <code className="rounded-md bg-surface-container-low px-2 py-1 text-[10px] text-on-surface-variant">{item.code}</code>
                    </td>
                    <td className="px-5 py-3 text-[10px] text-on-surface-variant">{item.color}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] ${item.active ? 'bg-primary-container text-on-primary-container' : 'bg-surface text-on-surface-variant'}`}>
                        {item.active ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[10px] text-on-surface-variant">{item.templateCount ?? 0}</td>
                    <td className="px-5 py-3">
                      {canManage ? (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title={editId === item.id ? 'Fechar edição' : 'Editar'}
                            onClick={() => (editId === item.id ? setEditId('') : startEdit(item))}
                            className="grid h-7 w-7 place-items-center rounded-lg border border-surface-container-high bg-white text-on-surface transition hover:bg-primary-container/20"
                          >
                            <span className="material-symbols-outlined text-[13px]">edit</span>
                          </button>
                          <button
                            type="button"
                            title={item.active ? 'Desativar' : 'Ativar'}
                            onClick={() => toggleActive(item)}
                            className="grid h-7 w-7 place-items-center rounded-lg border border-surface-container-high bg-white text-on-surface transition hover:bg-primary-container/20"
                          >
                            <span className="material-symbols-outlined text-[13px]">{item.active ? 'visibility_off' : 'visibility'}</span>
                          </button>
                          <button
                            type="button"
                            title="Excluir"
                            onClick={() => remove(item)}
                            className="grid h-7 w-7 place-items-center rounded-lg border border-error/20 bg-error-container/40 text-error transition hover:bg-error-container/70"
                          >
                            <span className="material-symbols-outlined text-[13px]">delete</span>
                          </button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-on-surface-variant">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
